import { Dispatch, FunctionComponent, SetStateAction } from "react";

import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";
import { ListSourceType } from "@/enum/list.enum";
import { useSnackBar } from "@/utils/useSnackbar";
import { useAddPeopleToTrackerByLinkedinUrlMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

interface UnassignedAddLeadModalProps {
	/** The lean list detail; its `sourceType` decides whether the add targets a tracker or the list. */
	list?: { id?: number; name?: string; sourceType?: ListSourceType };
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * "Add lead" launcher for the list detail toolbar. On a normal list it adds the selected people to
 * that list (the default `GlobalSearchModal` targetList flow). On a **tracker-source** list it must
 * instead add them to the tracker — otherwise the person only becomes a `list_person` and never gets
 * scored for signals (PREC-3676). There it opens the modal in confirm mode and routes the resolved
 * LinkedIn URLs to the tracker add endpoint, which creates the tracker organization + person and
 * schedules signal scoring.
 */
export const UnassignedAddLeadModal: FunctionComponent<UnassignedAddLeadModalProps> = ({
	list,
	isOpen,
	setIsOpen,
}) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [addPeopleToTracker] = useAddPeopleToTrackerByLinkedinUrlMutation();

	const isTrackerList = list?.sourceType === ListSourceType.TRACKER;

	const handleAddPeopleToTracker = async (linkedinUrls: string[]) => {
		if (!list?.id) {
			return;
		}

		const { addedCount } = await addPeopleToTracker({ listId: list.id, linkedinUrls }).unwrap();

		handleOpenSnackbar(
			`Added ${addedCount} ${addedCount === 1 ? "lead" : "leads"} to the tracker.`,
			6000
		);
	};

	return (
		<>
			{isOpen &&
				(isTrackerList ? (
					<GlobalSearchModal
						confirmLabel="Add to tracker"
						isOpened={isOpen}
						setIsOpened={setIsOpen}
						title="Add lead"
						onConfirmSelection={handleAddPeopleToTracker}
					/>
				) : (
					<GlobalSearchModal
						isOpened={isOpen}
						setIsOpened={setIsOpen}
						targetList={{ id: list?.id ?? 0, name: list?.name ?? "" }}
						title="Add lead"
					/>
				))}
			{renderMessageSnackbar()}
		</>
	);
};
